import { Fragment, useEffect, useState, useCallback } from "react";
import { useApiClient } from "../api/useApiClient";
import type { CfeFueraDeCarrilA } from "../types/reviewItem";
import { LineasRow, LineasToggle } from "../components/LineasRow";
import { CfeIdentidad } from "../components/CfeIdentidad";
import { TablaToolbar } from "../components/TablaToolbar";
import { Paginador } from "../components/Paginador";
import { ThOrdenable } from "../components/ThOrdenable";
import { abrirEnNuevaPestana } from "../utils/abrirEnNuevaPestana";
import { formatMonto, formatFecha } from "../utils/formato";
import { useTablaBandeja } from "../hooks/useTablaBandeja";
import { useContadores } from "../contexts/ContadoresContext";

const COLUMNAS = 8;

/**
 * Solo informativo — CFEs que no califican para Carril A (tipo no
 * soportado o RUT sin matriz, ver CLAUDE.md del backend). No se aprueban ni se
 * pueden "rescatar" a Carril A desde esta pantalla. Rol Contaduría únicamente
 * (el backend devuelve 403 con el rol Comprador/Aprobador).
 *
 * [ACTUALIZADO 10/08/2026] Estas filas SÍ tienen `status` (antes este
 * comentario decía que no): vale `FUERA_DE_CARRIL_A` o `EN_SAP`, lo segundo
 * cuando la reevaluación automática detecta que Contaduría ya cargó el CFE a
 * mano en SAP. Es triage, no una transición del pipeline — la fila se queda en
 * esta bandeja. Y un CFE puede salir de acá hacia Carril A, pero solo por la
 * reevaluación del backend (cuando el proveedor aparece en la matriz), nunca
 * por una acción de esta pantalla.
 *
 * [ACOTADO 14/08/2026] Es la bandeja de mayor volumen — creciendo sin techo
 * con cada backfill de poll_ucfe_inbox (2.540 filas / 1,64 MB medidos ese
 * día, ritmo de ~150-925 filas/día). El backend ahora devuelve como máximo
 * `FUERA_DE_CARRIL_A_TOPE` filas (1.000), las más recientes primero, dentro
 * de los últimos `REEVALUACION_VENTANA_DIAS` (90) — ver docs/Decisions.md
 * 14/08/2026. `api.obtenerContadores()` sigue devolviendo el TOTAL real (no
 * tiene tope), así que es lo que permite avisar cuánto quedó afuera sin
 * pedir una segunda lista completa solo para contar.
 */
export function FueraDeCarrilAPage() {
  const api = useApiClient();
  const { contadores } = useContadores();
  const [items, setItems] = useState<CfeFueraDeCarrilA[]>([]);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tabla = useTablaBandeja(items, {
    camposBusqueda: ["proveedor_nombre", "rut_emisor", "nro", "serie", "motivo_exclusion"],
    ordenInicial: { columna: "created_at", direccion: "desc" },
    camposNumericos: ["monto", "tipo_cfe"],
    claveEstable: "id",
  });

  // Cuántas ya están resueltas en SAP — se muestra arriba porque es el dato que
  // permite dimensionar cuánto de esta bandeja todavía necesita atención.
  const yaEnSap = items.filter((i) => i.status === "EN_SAP").length;

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await api.listarFueraDeCarrilA());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido al cargar la lista.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  function toggleExpandido(id: string) {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function abrirDocumento(id: string, tipo: "xml" | "pdf") {
    try {
      const url = await api.obtenerBlobSasUrl(id, tipo);
      abrirEnNuevaPestana(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`No se pudo obtener el ${tipo.toUpperCase()}:`, err);
      window.alert(`No se pudo abrir el ${tipo.toUpperCase()}. Ver consola para el detalle.`);
    }
  }

  if (loading) return <p>Cargando...</p>;
  if (error) return <p className="error-text">Error: {error}</p>;

  return (
    <div>
      <h1>Fuera de Carril A</h1>
      <p className="subtitle">
        Solo informativo — CFEs que no califican para Carril A hoy (tipo no soportado o proveedor sin matriz). No se
        aprueban ni se pueden reabrir desde acá.
        {yaEnSap > 0 && (
          <>
            {" "}
            De estos, <b>{yaEnSap}</b> ya fueron cargados a mano en SAP (marcados abajo) — no requieren acción.
          </>
        )}
      </p>
      {/* [NUEVO 14/08/2026] El backend acota esta lista (ver el comentario del
          componente) — sin este aviso, alguien vería 1.000 filas y asumiría
          que es todo lo que hay, en vez de que quedaron 1.540 sin mostrar. */}
      {!!contadores?.fuera_de_carril_a && contadores.fuera_de_carril_a > items.length && (
          <p className="subtitle">
            Mostrando las <b>{items.length}</b> más recientes de <b>{contadores.fuera_de_carril_a}</b> en total.
            Las más antiguas no se traen por defecto — usá el buscador para encontrar algo puntual dentro de lo
            mostrado.
          </p>
        )}

      <TablaToolbar
        busqueda={tabla.busqueda}
        onBuscar={tabla.setBusqueda}
        filtradas={tabla.filtradas}
        totalSinFiltro={items.length}
        placeholder="Buscar por proveedor, RUT, numero de CFE o motivo..."
      />

      <table className="compact">
        <colgroup>
          <col style={{ width: "15%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "22%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "5%" }} />
        </colgroup>
        <thead>
          <tr>
            <ThOrdenable columna="nro" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Documento
            </ThOrdenable>
            <ThOrdenable columna="proveedor_nombre" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Proveedor
            </ThOrdenable>
            <ThOrdenable columna="monto" orden={tabla.orden} onOrdenar={tabla.alternarOrden} alineado="derecha">
              Monto
            </ThOrdenable>
            <ThOrdenable columna="fecha_documento" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Fecha doc.
            </ThOrdenable>
            {/* Ordenable a propósito: permite agrupar de un click lo ya resuelto
                en SAP y separarlo de lo que sigue necesitando atención. */}
            <ThOrdenable columna="status" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Situación
            </ThOrdenable>
            <th>Motivo de exclusión</th>
            <th>Documentos</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tabla.filas.length === 0 && (
            <tr>
              <td colSpan={COLUMNAS} className="empty-row">
                {items.length === 0
                  ? "No hay CFEs fuera de Carril A."
                  : "Ningun CFE coincide con la busqueda."}
              </td>
            </tr>
          )}
          {tabla.filas.map((item) => (
            <Fragment key={item.id}>
              <tr>
                <td>
                  <CfeIdentidad
                    tipoCfe={item.tipo_cfe}
                    serie={item.serie}
                    nro={item.nro}
                    rutEmisor={item.rut_emisor}
                  />
                </td>
                <td className="wrap-ok" title={item.proveedor_nombre ?? undefined}>
                  {item.proveedor_nombre ?? "—"}
                </td>
                <td className="mono celda-monto">
                  {formatMonto(item.monto)}
                  {item.monto !== null && <span className="moneda">{item.moneda}</span>}
                </td>
                <td className="mono">{formatFecha(item.fecha_documento)}</td>
                <td>
                  {item.status === "EN_SAP" ? (
                    <span
                      className="badge badge-auth"
                      title="Contaduría ya cargó este CFE a mano en SAP — detectado por la reevaluación automática. No requiere acción."
                    >
                      Ya en SAP
                    </span>
                  ) : (
                    <span className="sin-resolver">Sin resolver</span>
                  )}
                </td>
                <td className="rejection-reason">{item.motivo_exclusion ?? "—"}</td>
                <td className="actions-cell">
                  <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "xml")}>
                    XML
                  </button>
                  {item.pdf_blob_url && (
                    <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "pdf")}>
                      PDF
                    </button>
                  )}
                </td>
                <td>
                  <LineasToggle expanded={expandidos.has(item.id)} onToggle={() => toggleExpandido(item.id)} />
                </td>
              </tr>
              {expandidos.has(item.id) && <LineasRow reviewItemId={item.id} colSpan={COLUMNAS} />}
            </Fragment>
          ))}
        </tbody>
      </table>

      <Paginador
        pagina={tabla.pagina}
        totalPaginas={tabla.totalPaginas}
        desde={tabla.desde}
        hasta={tabla.hasta}
        total={tabla.total}
        onIrAPagina={tabla.irAPagina}
      />
    </div>
  );
}
